import { Link } from "react-router-dom";
import { Search, Menu, X, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import SearchModal from "@/components/SearchModal";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useAuth } from "@/hooks/useAuth";
import { useAdminNotifications } from "@/hooks/useAdminNotifications";
import { useLangPath } from "@/hooks/useLangPath";

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { isAdmin } = useAuth();
  const { pendingCount } = useAdminNotifications(isAdmin);
  const { t } = useTranslation();
  const lp = useLangPath();

  const navLinks = [
    { to: lp("/episodes"), label: t("common.episodes") },
    { to: lp("/tournois"), label: t("common.tournaments") },
    { to: lp("/joueurs"), label: t("common.players") },
    { to: lp("/la-serie"), label: t("common.theSeries") },
  ];

  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:bg-accent focus:text-accent-foreground focus:px-4 focus:py-2 focus:rounded focus:text-sm focus:font-medium"
      >
        {t("common.skipToContent")}
      </a>
      <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-site items-center justify-between px-6">
          <Link to={lp("/")} className="flex items-center gap-1.5">
            <span className="text-lg font-bold tracking-tight font-display">
              DLTDP <span className="text-accent">Fan</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-8 md:flex" aria-label="Navigation principale">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                className="font-mono text-xs uppercase tracking-widest text-muted-foreground transition-colors hover:text-foreground"
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-1.5">
            <LanguageSwitcher />
            {isAdmin && (
              <Link
                to="/admin"
                className="relative inline-flex items-center gap-1 px-2 py-1 rounded bg-accent/15 text-accent text-[10px] font-semibold uppercase tracking-wide hover:bg-accent/25 transition-colors"
              >
                <ShieldCheck className="h-3 w-3" />
                {t("common.admin")}
                {pendingCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[14px] h-[14px] px-1 rounded-full bg-red-500 text-white text-[8px] font-bold leading-none">
                    {pendingCount}
                  </span>
                )}
              </Link>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-muted-foreground hover:text-foreground"
              onClick={() => setSearchOpen(!searchOpen)}
              aria-label={t("common.search")}
            >
              <Search className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden h-9 w-9"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={menuOpen ? t("common.closeMenu") : t("common.openMenu")}
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {menuOpen && (
          <div className="border-t border-border bg-background px-6 pb-4 pt-3 md:hidden animate-fade-in">
            <nav className="flex flex-col gap-3">
              {navLinks.map((link) => (
                <Link
                  key={link.label}
                  to={link.to}
                  onClick={() => setMenuOpen(false)}
                  className="font-mono text-xs uppercase tracking-widest text-muted-foreground transition-colors hover:text-foreground"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </header>

      <SearchModal open={searchOpen} onOpenChange={setSearchOpen} />
    </>
  );
};

export default Header;
